import {
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AvatarComponent } from '../avatar/avatar.component';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { MemberOption } from '../member-multiselect-input/member-multiselect-input.interfaces';

@Component({
  selector: 'ocp-member-select-panel',
  imports: [TranslatePipe, AvatarComponent, ThemedIconComponent],
  templateUrl: './member-select-panel.component.html',
  styleUrl: './member-select-panel.component.scss',
  host: { class: 'member-select-panel' },
})
export class MemberSelectPanelComponent {
  /** Available options */
  readonly options = input<MemberOption[]>([]);

  /** Currently selected IDs */
  readonly selectedIds = input<string[]>([]);

  /** Emits the toggled member ID */
  readonly memberToggled = output<string>();

  /** Emits when the panel requests to be closed (e.g. Escape key) */
  readonly closeRequested = output<void>();

  private readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly optionElements = viewChildren<ElementRef<HTMLButtonElement>>('optionElement');

  protected readonly searchQuery = signal('');
  protected readonly focusedIndex = signal(-1);

  protected readonly filteredOptions = computed(() => {
    const query = this.searchQuery().toLowerCase();
    if (!query) return this.options();
    return this.options().filter(
      o => o.label.toLowerCase().includes(query) || o.email?.toLowerCase().includes(query)
    );
  });

  focusSearch(): void {
    // Called by parent after popover opens
    setTimeout(() => {
      this.searchInputRef()?.nativeElement.focus({ preventScroll: true });
    });
  }

  reset(): void {
    this.searchQuery.set('');
    this.focusedIndex.set(-1);
  }

  protected isSelected(option: MemberOption): boolean {
    return this.selectedIds().includes(option.value);
  }

  protected toggleOption(option: MemberOption, event: Event): void {
    event.stopPropagation();
    this.memberToggled.emit(option.value);
  }

  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    this.focusedIndex.set(-1);
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.filteredOptions().length === 0) return;
        this.focusedIndex.set(0);
        this.focusOptionElement(0);
        break;
      case 'Escape':
        event.preventDefault();
        this.reset();
        this.closeRequested.emit();
        break;
    }
  }

  protected onOptionKeydown(event: KeyboardEvent, option: MemberOption, index: number): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleOption(option, event);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.navigateNext(index);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigatePrevious(index);
        break;
      case 'Escape':
        event.preventDefault();
        this.reset();
        this.closeRequested.emit();
        break;
    }
  }

  protected trackByValue(_index: number, option: MemberOption): string {
    return option.value;
  }

  private navigateNext(currentIndex: number): void {
    const nextIndex = (currentIndex + 1) % this.filteredOptions().length;
    this.focusedIndex.set(nextIndex);
    this.focusOptionElement(nextIndex);
  }

  private navigatePrevious(currentIndex: number): void {
    const prevIndex = currentIndex <= 0 ? this.filteredOptions().length - 1 : currentIndex - 1;
    this.focusedIndex.set(prevIndex);
    this.focusOptionElement(prevIndex);
  }

  private focusOptionElement(index: number): void {
    const elements = this.optionElements();
    if (elements[index]) {
      elements[index].nativeElement.focus();
    }
  }
}
